// M8-M: Componente actualizado para Programa de Atención NOM-035
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, CheckCircle, Clock, ChevronRight, Download, RefreshCw,
  Activity, Zap, FileCheck, ArrowLeft, Users, Calendar, ClipboardList,
  AlertTriangle, Check, X, Edit2, Save, Filter, Building2, User, FileText
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

// Constantes de estilos
const prioridadColors = {
  URGENTE: 'bg-red-100 text-red-800 border-red-300',
  ALTA: 'bg-orange-100 text-orange-800 border-orange-300',
  MEDIA: 'bg-yellow-100 text-yellow-800 border-yellow-300'
};

const estadoColors = {
  PENDIENTE: 'bg-gray-100 text-gray-700',
  EN_PROGRESO: 'bg-blue-100 text-blue-700',
  EVIDENCIADA: 'bg-green-100 text-green-700',
  COMPLETADA: 'bg-emerald-100 text-emerald-700',
  VENCIDA: 'bg-red-100 text-red-700'
};

const estadoProgramaColors = {
  borrador: 'bg-gray-100 text-gray-700 border-gray-300',
  configurado: 'bg-blue-100 text-blue-700 border-blue-300',
  aprobado: 'bg-green-100 text-green-700 border-green-300',
  en_ejecucion: 'bg-purple-100 text-purple-700 border-purple-300',
  completado: 'bg-emerald-100 text-emerald-700 border-emerald-300'
};

// Componente para editar campos obligatorios de una tarea (M8-M)
const TareaEditFields = ({ tarea, areas, usuarios, onSave, onCancel }) => {
  const { language } = useLanguage();
  const [areasSeleccionadas, setAreasSeleccionadas] = useState(tarea.areas_afectadas || ['TODA_EMPRESA']);
  const [responsableTipo, setResponsableTipo] = useState(tarea.responsable_ejecucion_user_id ? 'usuario' : 'texto');
  const [responsableUserId, setResponsableUserId] = useState(tarea.responsable_ejecucion_user_id || '');
  const [responsableTexto, setResponsableTexto] = useState(tarea.responsable_ejecucion_texto || tarea.responsable || '');
  const [fechaLimite, setFechaLimite] = useState(tarea.fecha_limite ? tarea.fecha_limite.split('T')[0] : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        areas_afectadas: areasSeleccionadas,
        responsable_ejecucion_user_id: responsableTipo === 'usuario' ? responsableUserId : null,
        responsable_ejecucion_texto: responsableTipo === 'texto' ? responsableTexto : null,
        fecha_limite: fechaLimite ? new Date(fechaLimite).toISOString() : null
      });
    } finally {
      setSaving(false);
    }
  };

  const texts = {
    es: {
      areas: 'Área(s) afectada(s)',
      allCompany: 'Toda la empresa',
      responsible: 'Responsable de ejecución',
      platformUser: 'Usuario de plataforma',
      freeText: 'Texto libre',
      selectUser: 'Seleccionar usuario...',
      deadline: 'Fecha límite',
      save: 'Guardar',
      cancel: 'Cancelar'
    },
    en: {
      areas: 'Affected area(s)',
      allCompany: 'All company',
      responsible: 'Execution responsible',
      platformUser: 'Platform user',
      freeText: 'Free text',
      selectUser: 'Select user...',
      deadline: 'Deadline',
      save: 'Save',
      cancel: 'Cancel'
    }
  };
  const t = texts[language] || texts.es;

  return (
    <div className="space-y-4 p-4 bg-white border rounded-lg shadow-sm">
      {/* Áreas afectadas */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Building2 className="w-4 h-4" /> {t.areas} *
        </Label>
        {areas.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {areas.map(area => (
              <Button
                key={area.id}
                size="sm"
                variant={areasSeleccionadas.includes(area.id) ? "default" : "outline"}
                onClick={() => {
                  if (areasSeleccionadas.includes(area.id)) {
                    setAreasSeleccionadas(areasSeleccionadas.filter(a => a !== area.id));
                  } else {
                    setAreasSeleccionadas([...areasSeleccionadas.filter(a => a !== 'TODA_EMPRESA'), area.id]);
                  }
                }}
              >
                {area.nombre}
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-blue-50">
              <Building2 className="w-3 h-3 mr-1" /> {t.allCompany}
            </Badge>
            <span className="text-xs text-gray-500">(No hay organigrama configurado)</span>
          </div>
        )}
      </div>

      {/* Responsable */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <User className="w-4 h-4" /> {t.responsible} *
        </Label>
        <div className="flex gap-2 mb-2">
          <Button
            size="sm"
            variant={responsableTipo === 'usuario' ? "default" : "outline"}
            onClick={() => setResponsableTipo('usuario')}
          >
            {t.platformUser}
          </Button>
          <Button
            size="sm"
            variant={responsableTipo === 'texto' ? "default" : "outline"}
            onClick={() => setResponsableTipo('texto')}
          >
            {t.freeText}
          </Button>
        </div>
        {responsableTipo === 'usuario' ? (
          <Select value={responsableUserId} onValueChange={setResponsableUserId}>
            <SelectTrigger>
              <SelectValue placeholder={t.selectUser} />
            </SelectTrigger>
            <SelectContent>
              {usuarios.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.nombre} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={responsableTexto}
            onChange={(e) => setResponsableTexto(e.target.value)}
            placeholder={language === 'es' ? 'Nombre del responsable...' : 'Responsible name...'}
          />
        )}
      </div>

      {/* Fecha límite */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Calendar className="w-4 h-4" /> {t.deadline} *
        </Label>
        <Input
          type="date"
          value={fechaLimite}
          onChange={(e) => setFechaLimite(e.target.value)}
        />
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" /> {t.save}
        </Button>
      </div>
    </div>
  );
};

// Componente principal del Programa de Atención M8-M
const ProgramaAtencionM8M = ({ open, onOpenChange, empresaId, onComplete }) => {
  const { language } = useLanguage();
  const [planes, setPlanes] = useState([]);
  const [stats, setStats] = useState({});
  const [estadoPrograma, setEstadoPrograma] = useState('borrador');
  const [puedeAprobar, setPuedeAprobar] = useState(false);
  const [accionesIncompletas, setAccionesIncompletas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [aprobando, setAprobando] = useState(false);
  
  // Vista de detalle
  const [showTareaDetail, setShowTareaDetail] = useState(false);
  const [tareaDetail, setTareaDetail] = useState(null);
  const [editingTarea, setEditingTarea] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [updatingTarea, setUpdatingTarea] = useState(false);
  
  // Formulario de tarea
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [nuevoProgreso, setNuevoProgreso] = useState(0);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [notas, setNotas] = useState([]);
  
  // Filtros M8-M
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroArea, setFiltroArea] = useState('todas');
  const [filtroResponsable, setFiltroResponsable] = useState('todos');
  
  // Datos auxiliares
  const [areas, setAreas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const estadoLabels = {
    PENDIENTE: language === 'es' ? 'Pendiente' : 'Pending',
    EN_PROGRESO: language === 'es' ? 'En Progreso' : 'In Progress',
    EVIDENCIADA: language === 'es' ? 'Evidenciada' : 'Evidenced',
    COMPLETADA: language === 'es' ? 'Completada' : 'Completed',
    VENCIDA: language === 'es' ? 'Vencida' : 'Overdue'
  };

  const estadoProgramaLabels = {
    borrador: language === 'es' ? 'Borrador' : 'Draft',
    configurado: language === 'es' ? 'Configurado' : 'Configured',
    aprobado: language === 'es' ? 'Aprobado' : 'Approved',
    en_ejecucion: language === 'es' ? 'En Ejecución' : 'In Progress',
    completado: language === 'es' ? 'Completado' : 'Completed'
  };

  useEffect(() => {
    if (open) {
      loadPrograma();
      loadAuxData();
    }
  }, [open]);

  const loadPrograma = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/programa-atencion?lang=${language}`);
      if (res.data.success) {
        setPlanes(res.data.data.planes || []);
        setStats(res.data.data.stats || {});
        setEstadoPrograma(res.data.data.estado_programa || 'borrador');
        setPuedeAprobar(res.data.data.puede_aprobar || false);
        setAccionesIncompletas(res.data.data.acciones_incompletas || 0);
      }
    } catch (error) {
      toast.error('Error loading program');
    } finally {
      setLoading(false);
    }
  };

  const loadAuxData = async () => {
    try {
      // Cargar áreas del organigrama
      const areasRes = await axios.get(`/organization/areas/${empresaId}`).catch(() => ({ data: { data: [] } }));
      setAreas(areasRes.data?.data || []);
      
      // Cargar usuarios de la empresa (usando endpoint NOM-035)
      const usersRes = await axios.get(`/v5/nom035/${empresaId}/usuarios`).catch(() => ({ data: { data: [] } }));
      setUsuarios(usersRes.data?.data || []);
    } catch (error) {
      logger.error('Error loading aux data:', error);
    }
  };

  const loadTareaDetail = async (tareaId) => {
    setLoadingDetail(true);
    try {
      const [tareaRes, notasRes] = await Promise.all([
        axios.get(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaId}?lang=${language}`),
        axios.get(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaId}/notas`).catch(() => ({ data: { data: { notas: [] } } }))
      ]);
      
      if (tareaRes.data.success) {
        setTareaDetail(tareaRes.data.data);
        setNuevoProgreso(tareaRes.data.data.progreso || 0);
        setNuevoEstado(tareaRes.data.data.estado || 'PENDIENTE');
        setNotas(notasRes.data?.data?.notas || []);
        setShowTareaDetail(true);
      }
    } catch (error) {
      toast.error('Error loading task details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateTarea = async () => {
    if (!tareaDetail) return;
    setUpdatingTarea(true);
    try {
      const res = await axios.put(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaDetail.id}`, {
        estado: nuevoEstado,
        progreso: nuevoProgreso,
        comentario: nuevoComentario.trim() || null
      });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Tarea actualizada' : 'Task updated');
        setNuevoComentario('');
        loadTareaDetail(tareaDetail.id);
        loadPrograma();
        onComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating task');
    } finally {
      setUpdatingTarea(false);
    }
  };

  const handleUpdateTareaCampos = async (tareaId, campos) => {
    try {
      const res = await axios.put(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaId}/campos`, campos);
      if (res.data.success) {
        toast.success(language === 'es' ? 'Campos actualizados' : 'Fields updated');
        setEditingTarea(null);
        loadPrograma();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating fields');
    }
  };

  const handleAddNota = async () => {
    if (!tareaDetail || !nuevoComentario.trim()) return;
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaDetail.id}/notas`, {
        texto: nuevoComentario.trim()
      });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Nota agregada' : 'Note added');
        setNuevoComentario('');
        loadTareaDetail(tareaDetail.id);
      }
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const handleUploadEvidencia = async (file) => {
    if (!tareaDetail || !file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaDetail.id}/evidencia`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        toast.success(language === 'es' ? 'Evidencia subida' : 'Evidence uploaded');
        loadTareaDetail(tareaDetail.id);
        loadPrograma();
        onComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading evidence');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/programa-atencion/generate`);
      if (res.data.success) {
        toast.success(res.data.message);
        loadPrograma();
        onComplete?.();
      }
    } catch (error) {
      toast.error('Error generating program');
    } finally {
      setGenerating(false);
    }
  };

  const handleAprobarPrograma = async () => {
    if (accionesIncompletas > 0) {
      toast.error(language === 'es' 
        ? `Hay ${accionesIncompletas} acciones sin campos obligatorios completos`
        : `There are ${accionesIncompletas} actions without complete required fields`
      );
      return;
    }
    
    setAprobando(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/programa-atencion/aprobar`);
      if (res.data.success) {
        toast.success(language === 'es' ? 'Programa aprobado correctamente' : 'Program approved successfully');
        loadPrograma();
        onComplete?.();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error approving program');
    } finally {
      setAprobando(false);
    }
  };

  // Filtrar planes/tareas
  const filteredPlanes = planes.map(plan => ({
    ...plan,
    tareas: plan.tareas?.filter(tarea => {
      if (filtroEstado !== 'todos' && tarea.estado !== filtroEstado) return false;
      if (filtroArea !== 'todas' && !tarea.areas_afectadas?.includes(filtroArea)) return false;
      if (filtroResponsable !== 'todos') {
        const responsable = tarea.responsable_ejecucion_user_id || tarea.responsable_ejecucion_texto || tarea.responsable || '';
        if (!responsable.toLowerCase().includes(filtroResponsable.toLowerCase())) return false;
      }
      return true;
    }) || []
  })).filter(plan => plan.tareas.length > 0 || filtroEstado === 'todos');

  // Vista de detalle de tarea
  if (showTareaDetail && tareaDetail) {
    const esInmutable = tareaDetail.titulo_inmutable && estadoPrograma !== 'borrador';
    
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setShowTareaDetail(false); setTareaDetail(null); } onOpenChange(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowTareaDetail(false); setTareaDetail(null); }}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-teal-600" />
                {language === 'es' ? 'Detalle de Acción' : 'Action Detail'}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info de la tarea */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <h3 className="font-semibold text-lg">{tareaDetail.titulo}</h3>
              <p className="text-sm text-gray-600 mt-1">{tareaDetail.descripcion || tareaDetail.tipo_evidencia}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge className={estadoColors[tareaDetail.estado]}>{estadoLabels[tareaDetail.estado]}</Badge>
                <Badge variant="outline">{tareaDetail.dominio || tareaDetail.dominio_nom035}</Badge>
                <Badge variant="outline">{tareaDetail.nivel_riesgo}</Badge>
              </div>
              
              {/* M8-M: Campos obligatorios */}
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{language === 'es' ? 'Áreas:' : 'Areas:'}</span>
                  <span className="font-medium">
                    {tareaDetail.areas_afectadas?.length > 0 
                      ? tareaDetail.areas_afectadas.join(', ')
                      : (language === 'es' ? 'Sin asignar' : 'Not assigned')}
                  </span>
                  {!tareaDetail.campos_completos && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{language === 'es' ? 'Responsable:' : 'Responsible:'}</span>
                  <span className="font-medium">
                    {tareaDetail.responsable || (language === 'es' ? 'Sin asignar' : 'Not assigned')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{language === 'es' ? 'Fecha límite:' : 'Deadline:'}</span>
                  <span className={`font-medium ${tareaDetail.estado === 'VENCIDA' ? 'text-red-600' : ''}`}>
                    {tareaDetail.fecha_limite 
                      ? new Date(tareaDetail.fecha_limite).toLocaleDateString()
                      : (language === 'es' ? 'Sin asignar' : 'Not assigned')}
                  </span>
                </div>
              </div>
              
              {esInmutable && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                  ⚠ {language === 'es' 
                    ? 'El título y descripción no son editables después de aprobar el programa.'
                    : 'Title and description are not editable after program approval.'}
                </div>
              )}
            </div>

            {/* Progreso */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{language === 'es' ? 'Progreso' : 'Progress'}</Label>
                <span className="text-sm font-semibold text-teal-600">{nuevoProgreso}%</span>
              </div>
              <Slider
                value={[nuevoProgreso]}
                onValueChange={(value) => setNuevoProgreso(value[0])}
                max={100}
                step={5}
                className="w-full"
                disabled={tareaDetail.estado === 'EVIDENCIADA' || tareaDetail.estado === 'COMPLETADA'}
              />
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
              <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDIENTE">{estadoLabels.PENDIENTE}</SelectItem>
                  <SelectItem value="EN_PROGRESO">{estadoLabels.EN_PROGRESO}</SelectItem>
                  <SelectItem value="EVIDENCIADA">{estadoLabels.EVIDENCIADA}</SelectItem>
                  <SelectItem value="COMPLETADA">{estadoLabels.COMPLETADA}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subir evidencia */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Subir Evidencia' : 'Upload Evidence'}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  onChange={(e) => e.target.files?.[0] && handleUploadEvidencia(e.target.files[0])}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                {language === 'es' 
                  ? 'M8-M: Las evidencias no pueden eliminarse una vez adjuntadas.'
                  : 'M8-M: Evidence cannot be deleted once attached.'}
              </p>
            </div>

            {/* Evidencias existentes */}
            {tareaDetail.evidencias && tareaDetail.evidencias.length > 0 && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Evidencias subidas' : 'Uploaded evidence'}</Label>
                <div className="space-y-1">
                  {tareaDetail.evidencias.map((ev, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                      <FileCheck className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{ev.nombre_archivo || `Evidencia ${idx + 1}`}</span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {new Date(ev.fecha_subida).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* M8-M: Notas de seguimiento (Bitácora) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {language === 'es' ? 'Notas de Seguimiento' : 'Follow-up Notes'}
              </Label>
              <Textarea
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder={language === 'es' ? 'Escribe una nota de seguimiento...' : 'Write a follow-up note...'}
                rows={2}
              />
              <Button size="sm" variant="outline" onClick={handleAddNota} disabled={!nuevoComentario.trim()}>
                {language === 'es' ? 'Agregar Nota' : 'Add Note'}
              </Button>
              
              {/* Historial de notas */}
              {notas.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto mt-2">
                  {notas.map((nota, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded border text-sm">
                      <p>{nota.texto}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {nota.usuario} — {new Date(nota.fecha).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">
                {language === 'es' 
                  ? 'M8-M: Las notas no son editables ni eliminables.'
                  : 'M8-M: Notes cannot be edited or deleted.'}
              </p>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowTareaDetail(false); setTareaDetail(null); }}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={handleUpdateTarea} disabled={updatingTarea} className="bg-teal-600 hover:bg-teal-700">
                {updatingTarea && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Vista principal del programa
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              {language === 'es' ? 'Programa de Atención NOM-035' : 'NOM-035 Attention Program'}
            </DialogTitle>
            <Badge className={estadoProgramaColors[estadoPrograma]}>
              {estadoProgramaLabels[estadoPrograma]}
            </Badge>
          </div>
          <DialogDescription>
            {language === 'es' 
              ? 'M8-M: Gestión de acciones con áreas, responsables y fechas obligatorias'
              : 'M8-M: Action management with required areas, responsibles and dates'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              <div className="text-center p-2 bg-gray-50 rounded">
                <span className="text-xl font-bold">{stats.total || 0}</span>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <span className="text-xl font-bold text-green-700">{stats.evidenciadas || 0}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Evidenciadas' : 'Evidenced'}</p>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded">
                <span className="text-xl font-bold text-yellow-700">{(stats.pendientes || 0) + (stats.en_progreso || 0)}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Pendientes' : 'Pending'}</p>
              </div>
              <div className="text-center p-2 bg-red-50 rounded">
                <span className="text-xl font-bold text-red-700">{stats.vencidas || 0}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Vencidas' : 'Overdue'}</p>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded">
                <span className="text-xl font-bold text-orange-700">{accionesIncompletas}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Incompletas' : 'Incomplete'}</p>
              </div>
            </div>

            {/* M8-M: Barra de aprobación */}
            {planes.length > 0 && estadoPrograma === 'borrador' && (
              <div className={`p-3 rounded-lg border mb-4 ${accionesIncompletas > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${accionesIncompletas > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                      {accionesIncompletas > 0 
                        ? (language === 'es' ? `⚠ ${accionesIncompletas} acciones requieren completar campos obligatorios` : `⚠ ${accionesIncompletas} actions need required fields`)
                        : (language === 'es' ? '✓ Todas las acciones tienen los campos obligatorios' : '✓ All actions have required fields')}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {language === 'es' 
                        ? 'Para aprobar: cada acción necesita área(s), responsable y fecha límite'
                        : 'To approve: each action needs area(s), responsible and deadline'}
                    </p>
                  </div>
                  <Button 
                    onClick={handleAprobarPrograma} 
                    disabled={aprobando || accionesIncompletas > 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {aprobando && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                    <Check className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Aprobar Programa' : 'Approve Program'}
                  </Button>
                </div>
              </div>
            )}

            {/* M8-M: Filtros */}
            {planes.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 p-2 bg-gray-50 rounded border">
                <div className="flex items-center gap-1">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">{language === 'es' ? 'Filtros:' : 'Filters:'}</span>
                </div>
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue placeholder={language === 'es' ? 'Estado' : 'Status'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                    <SelectItem value="PENDIENTE">{estadoLabels.PENDIENTE}</SelectItem>
                    <SelectItem value="EN_PROGRESO">{estadoLabels.EN_PROGRESO}</SelectItem>
                    <SelectItem value="EVIDENCIADA">{estadoLabels.EVIDENCIADA}</SelectItem>
                    <SelectItem value="VENCIDA">{estadoLabels.VENCIDA}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Generate button */}
            {planes.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed rounded-lg mb-4">
                <Activity className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 mb-4">
                  {language === 'es' 
                    ? 'No hay planes generados. Genera el programa basado en los resultados de Guía III.'
                    : 'No plans generated. Generate program based on Guide III results.'}
                </p>
                <Button onClick={handleGenerate} disabled={generating}>
                  {generating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  <Zap className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Generar Programa' : 'Generate Program'}
                </Button>
              </div>
            ) : (stats.total === 0 || planes.every(p => !p.tareas || p.tareas.length === 0)) && (
              <div className="text-center py-4 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg mb-4">
                <RefreshCw className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                <p className="text-yellow-700 mb-3">
                  {language === 'es' 
                    ? 'Los planes existen pero no tienen acciones. Regenera para crear las acciones.'
                    : 'Plans exist but have no actions. Regenerate to create actions.'}
                </p>
                <Button onClick={handleGenerate} disabled={generating} variant="outline" className="border-yellow-500 text-yellow-700 hover:bg-yellow-100">
                  {generating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  <Zap className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Regenerar Acciones' : 'Regenerate Actions'}
                </Button>
              </div>
            )}

            {/* Plans list */}
            <div className="space-y-4">
              {filteredPlanes.map(plan => (
                <div key={plan.id} className={`border rounded-lg p-4 ${prioridadColors[plan.prioridad] || 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge>{plan.prioridad}</Badge>
                        {plan.estado_programa && (
                          <Badge variant="outline" className={estadoProgramaColors[plan.estado_programa]}>
                            {estadoProgramaLabels[plan.estado_programa]}
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium mt-1">{plan.dominio}</h4>
                      <p className="text-xs text-gray-600">
                        {language === 'es' ? 'Capital MEVIA:' : 'MEVIA Capital:'} {plan.capital_mevia}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className={
                        plan.nivel_riesgo === 'MUY_ALTO' ? 'bg-red-100 text-red-800' :
                        plan.nivel_riesgo === 'ALTO' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>{plan.nivel_riesgo}</Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {plan.empleados_afectados} {language === 'es' ? 'empleados' : 'employees'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {language === 'es' ? 'Límite:' : 'Deadline:'} {plan.fecha_limite ? new Date(plan.fecha_limite).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Tasks - clickable with edit option */}
                  <div className="space-y-2 mt-3">
                    {plan.tareas?.map(tarea => (
                      <div key={tarea.id}>
                        {editingTarea === tarea.id ? (
                          <TareaEditFields
                            tarea={tarea}
                            areas={areas}
                            usuarios={usuarios}
                            onSave={(campos) => handleUpdateTareaCampos(tarea.id, campos)}
                            onCancel={() => setEditingTarea(null)}
                          />
                        ) : (
                          <div 
                            className={`flex items-center justify-between p-3 bg-white rounded border hover:border-teal-400 hover:shadow-sm transition-all ${!tarea.campos_completos ? 'border-yellow-300' : ''}`}
                            data-testid={`tarea-${tarea.id}`}
                          >
                            <div 
                              className="flex items-center gap-3 flex-1 cursor-pointer"
                              onClick={() => loadTareaDetail(tarea.id)}
                            >
                              {tarea.estado === 'EVIDENCIADA' || tarea.estado === 'COMPLETADA' ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : tarea.estado === 'VENCIDA' ? (
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                              ) : tarea.estado === 'EN_PROGRESO' ? (
                                <Clock className="w-5 h-5 text-blue-500" />
                              ) : (
                                <Clock className="w-5 h-5 text-gray-400" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium">{tarea.titulo}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-xs text-gray-500">{tarea.tipo_evidencia}</p>
                                  {!tarea.campos_completos && (
                                    <span className="text-xs text-yellow-600 flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      {language === 'es' ? 'Campos incompletos' : 'Incomplete fields'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Badge className={`text-xs ${estadoColors[tarea.estado] || 'bg-gray-100'}`}>
                                {estadoLabels[tarea.estado] || tarea.estado}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              {/* M8-M: Botón para editar campos */}
                              {estadoPrograma === 'borrador' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); setEditingTarea(tarea.id); }}
                                  title={language === 'es' ? 'Editar campos' : 'Edit fields'}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Tip */}
            {planes.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>💡 M8-M:</strong> {language === 'es' 
                    ? 'Haz clic en el lápiz (✏) para asignar áreas, responsable y fecha. Haz clic en la tarea para ver detalles y subir evidencias.'
                    : 'Click the pencil (✏) to assign areas, responsible and date. Click the task to view details and upload evidence.'}
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramaAtencionM8M;
